/**
 * admin/src/hooks/useContent.ts
 * Hook cho Content (Quiz Catalog) management
 *
 * useContent      — danh sách quiz catalog, server-side filter
 * useContentMut   — PATCH access_tier / is_published toggle
 */

import { useState, useEffect, useCallback } from 'react';
import { adminFetch } from './useAdmin';
import { toast } from '../components/ui/index';
import type {
  AdminQuizCatalogRow,
  AdminContentResponse,
  AccessTier,
  CreateLessonPayload,
} from '../types/admin';

// ─── Params ───────────────────────────────────────────────────────────────────
export interface ContentParams {
  page?: number;
  limit?: number;
  search?: string;
  pathway?: string;
  subject?: string;
  skill?: string;
  tier?: AccessTier | '';
}

interface UseContentReturn {
  items: AdminQuizCatalogRow[];
  total: number;
  totalPages: number;
  pathways: string[];
  subjects: string[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useContent(params: ContentParams = {}): UseContentReturn {
  const [data, setData] = useState<AdminContentResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    adminFetch<AdminContentResponse>('/content', {
      params: {
        page: params.page ?? 1,
        limit: params.limit ?? 50,
        search: params.search || undefined,
        pathway: params.pathway || undefined,
        subject: params.subject || undefined,
        skill: params.skill || undefined,
        tier: params.tier || undefined,
      },
    })
      .then((res) => { if (!cancelled) { setData(res); setIsLoading(false); } })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Lỗi tải content');
          setIsLoading(false);
        }
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params), tick]);

  return {
    items: data?.data ?? [],
    total: data?.total ?? 0,
    totalPages: data?.total_pages ?? 1,
    pathways: data?.filters.pathways ?? [],
    subjects: data?.filters.subjects ?? [],
    isLoading,
    error,
    refetch,
  };
}

// ─── Mutations ────────────────────────────────────────────────────────────────
interface PatchPayload {
  access_tier?: AccessTier;
  is_published?: boolean;
  show_on_home?: boolean;
}

export function useContentMutations(onSuccess?: () => void) {
  const [savingId, setSavingId] = useState<string | null>(null);

  async function patchQuiz(quizId: string, payload: PatchPayload) {
    setSavingId(quizId);
    try {
      await adminFetch(`/content/${quizId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });

      const label = payload.access_tier
        ? `access_tier → ${payload.access_tier}`
        : payload.is_published !== undefined
        ? payload.is_published ? 'đã publish' : 'đã ẩn'
        : 'đã cập nhật';

      toast(`${quizId}: ${label}`, 'success');
      onSuccess?.();
    } catch (err) {
      toast(err instanceof Error ? err.message : `Lỗi update ${quizId}`, 'error');
    } finally {
      setSavingId(null);
    }
  }

  return { patchQuiz, savingId };
}

// ─── Create Lesson ────────────────────────────────────────────────────────────
export function useCreateLesson(onSuccess?: () => void) {
  const [isCreating, setIsCreating] = useState(false);

  async function createLesson(payload: CreateLessonPayload): Promise<boolean> {
    setIsCreating(true);
    try {
      await adminFetch('/content', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      toast(`✅ Đã tạo bài: ${payload.quiz_id}`, 'success');
      onSuccess?.();
      return true;
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Lỗi tạo bài', 'error');
      return false;
    } finally {
      setIsCreating(false);
    }
  }

  return { createLesson, isCreating };
}
